import { Component, OnInit, ViewChild } from '@angular/core';
import { ABCisTableModel, ColumnType, AbcisTableComponent } from '../../../../components/abcis-table/abcis-table.component';
import { ABCisApi, ApiService } from '../../../../handlers/apiHandler.service';
import { FormControl, Validators, FormGroup, FormBuilder } from '@angular/forms';
import { Observable, from, of } from 'rxjs';
import { map, startWith, debounceTime, switchMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-historical-line-item-config',
  standalone: false,
  templateUrl: './historical-line-item-config.component.html',
  styleUrls: ['./historical-line-item-config.component.scss']
})
export class HistoricalLineItemConfigComponent implements OnInit {
  @ViewChild(AbcisTableComponent) historicalTable: AbcisTableComponent;

  formGroup: FormGroup;
  tableConfig: ABCisTableModel;

  tableVisible = false;
  storeControl = new FormControl();
  supplierControl = new FormControl();
  pluControl = new FormControl();
  stores: Array<Store> = [];
  suppliers: Array<Supplier> = [];
  filteredStores: Observable<Store[]>;
  filteredSupplier: Observable<Supplier[]>;
  selectedStoreId: number | null = null;
  selectedSupplier: Supplier | null = null; // Track selected supplier
  isApplyingBulkAction = false;

  constructor(private apiHelper: ApiService, private fb: FormBuilder, private toastr: ToastrService) {}

  ngOnInit() {
    // Initialize the form group with validation
    this.formGroup = this.fb.group({
      storeControl: ['', Validators.required],
      supplierControl: [''],
      pluControl: [''],
    });

    // Add conditional validation
    this.formGroup.get('supplierControl').valueChanges.subscribe(() => {
      this._updateControlValidation();
    });

    this.formGroup.get('pluControl').valueChanges.subscribe(() => {
      this._updateControlValidation();
    });

    // Fetch and set the store options
    this.apiHelper.getABCisRequest<Array<Store>>(ABCisApi.Support, 'store').then((response) => {
      this.stores = response.body;
    });

    // Setup value changes and filtering for stores and suppliers
    this.filteredStores = this.formGroup.get('storeControl').valueChanges.pipe(
      startWith(''),
      map(value => this._filterStores(value))
    );

    this.filteredSupplier = this.formGroup.get('supplierControl').valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      switchMap(value => this._fetchFilteredSuppliers(value))
    );

    this.formGroup.get('storeControl').valueChanges.subscribe(value => {
      const selectedStore = this.stores.find(store => store.storeName === value);
      this.selectedStoreId = selectedStore ? selectedStore.storeId : null;
      // Reset supplier control only if there is a change
      const supplierControl = this.formGroup.get('supplierControl');
      if (supplierControl.value) {
        supplierControl.reset('', { emitEvent: false }); // Avoid emitting event to prevent infinite loop
      }
      // Update the supplier control's validity once, and only if necessary
      setTimeout(() => {
        if (supplierControl.invalid) {
          supplierControl.updateValueAndValidity({ emitEvent: false });
        }
      });
    });

    // Update selected supplier when supplier control value changes
    this.formGroup.get('supplierControl').valueChanges.subscribe(value => {
      console.log('Supplier control changed:', value);
      this.selectedSupplier = this.suppliers.find(supplier => supplier.label === value) || null;
      this._updateControlValidation();
    });

  }

  private _fetchFilteredSuppliers(value: string): Observable<Supplier[]> {
    console.log('Fetching filtered suppliers:', value);

    if (!value || !this.selectedStoreId) {
      return of([]); // Return an empty observable if the input value is empty or no store selected
    }

    // Check if the input value is already in the suppliers list
    const existingSupplier = this.suppliers.find(supplier => supplier.label.toLowerCase() === value.toLowerCase());
    if (existingSupplier) {
      return of(this.suppliers);
    }

    const url = `store-supplier/lookup-temp?mode=filter&values=${value}&storeIds=${this.selectedStoreId}`;
    return from(
      this.apiHelper.getABCisRequest<Array<Supplier>>(ABCisApi.Support, url)
        .then(response => {
          this.suppliers = response.body;
          return this.suppliers;
        })
    );
  }

  setHistoricalTable(storeId: number | null, supplierValue: number | null, plu: string) {
    const urlParameter = [{ name: 'storeId', value: `${storeId}` }];

    if (supplierValue != null) {
      urlParameter.push({ name: 'supplierId', value: `${supplierValue}` });
    }
    if (plu) {
      urlParameter.push({ name: 'plu', value: plu });
    }

    this.tableConfig = {
      apiConfig: {
        api: ABCisApi.Support,
        table: 'null',
        url: 'historical-line-item',
        urlParams: urlParameter,
        approxamateRowCount: 1000,
        anyParams: true
      },
      colConfig: [
        { field: 'lineItemMapId', header: 'Line Item Map ID', type: ColumnType.String, isReadonly: true, isPrimary: true, notInForm: true },
        { field: 'storeId', header: 'Store ID', type: ColumnType.String, isReadonly: true },
        { field: 'storeSupplierId', header: 'Store Supplier', type: ColumnType.MultiSelectComplex, isReadonly: true,  setUrl: 'store-supplier/lookup-temp', addedComplexFields: ['storeId'],
          isSingleSelect: true , isFilterSingleSelect: true, isFilterBackendSearch: false, addFormValidators: [Validators.required]},
        { field: 'plu', header: 'PLU', type: ColumnType.String, isReadonly: true },
        { field: 'ocrSupplierReference', header: 'OCR Supplier Reference', type: ColumnType.String, isReadonly: true },
        { field: 'ocrDescription', header: 'OCR Description', type: ColumnType.String, isReadonly: true },
        { field: 'storeSupplierReference', header: 'Store Supplier Reference', type: ColumnType.String, isReadonly: true },
        { field: 'storeDescription', header: 'Store Description', type: ColumnType.String, isReadonly: true },
        { field: 'invoiceDetailId', header: 'Invoice Detail ID', type: ColumnType.String, isReadonly: true },
        { field: 'documentLineItemId', header: 'Document Line Item ID', type: ColumnType.String, isReadonly: true },
        { field: 'excludeHistoricalMatching', header: 'Exclude Historical Matching', type: ColumnType.Boolean },
        { field: 'excludeMultiplierMatching', header: 'Exclude Multiplier Matching', type: ColumnType.Boolean },
        { field: 'receiveDate', header: 'Receive Date', type: ColumnType.Date, isReadonly: true },
      ],
      isConfigurable: true,
      canAdd: false,
      canDelete: false,
      initialRowCount: 10
    };
  }

  private _filterStores(value: string): Store[] {
    const filterValue = value.toLowerCase();
    return this.stores.filter(store => store.storeName.toLowerCase().includes(filterValue));
  }

  onSubmit() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const selectedStore = this.stores.find(store => store.storeName === this.formGroup.get('storeControl').value);
    const selectedSupplier = this.suppliers.find(supplier => supplier.label === this.formGroup.get('supplierControl').value);
    const storeId = selectedStore ? selectedStore.storeId : null;
    const supplierValue = selectedSupplier ? selectedSupplier.value : null;
    const plu = this.formGroup.get('pluControl').value;
    let formattedPlu;

    if (plu) {
      formattedPlu = plu.toString().padStart(14, '0');
    }

    this.tableVisible = true;
    this.setHistoricalTable(storeId, supplierValue, formattedPlu);
  }

  async excludeHistoricalMatches() {
    await this.applyBulkFlag('historical-line-item/exclude-historical-matching', 'historical matching');
  }

  async excludeMultiplierMatches() {
    await this.applyBulkFlag('historical-line-item/exclude-multiplier-matching', 'multiplier matching');
  }

  canApplyBulkAction(): boolean {
    return this.tableVisible && !!this.tableConfig && !this.isApplyingBulkAction;
  }

  private async applyBulkFlag(url: string, label: string) {
    if (!this.canApplyBulkAction()) {
      return;
    }

    const recordCount = this.getBulkRecordCount();
    const countLabel = recordCount === null ? 'all matching records' : `${recordCount} matching record${recordCount === 1 ? '' : 's'}`;
    const hasConfirmed = confirm(`Are you sure you want to exclude ${label} for ${countLabel}?`);
    if (!hasConfirmed) {
      return;
    }

    this.isApplyingBulkAction = true;

    try {
      await this.apiHelper.postABCisRequest(ABCisApi.Support, url, this.buildBulkUpdatePayload());
      this.historicalTable?.RefreshTable();
      this.toastr.success(`Exclude ${label} update submitted successfully.`, 'Success');
    } catch (error) {
      const httpError = error as HttpErrorResponse;
      this.toastr.error(httpError?.error || `Failed to update exclude ${label}.`, 'Error');
    } finally {
      this.isApplyingBulkAction = false;
    }
  }

  private buildBulkUpdatePayload() {
    const selectedStore = this.stores.find(store => store.storeName === this.formGroup.get('storeControl').value);
    const selectedSupplier = this.suppliers.find(supplier => supplier.label === this.formGroup.get('supplierControl').value);
    const plu = this.formGroup.get('pluControl').value;
    const formattedPlu = plu ? plu.toString().padStart(14, '0') : null;

    return {
      storeId: selectedStore ? selectedStore.storeId : null,
      supplierId: selectedSupplier ? selectedSupplier.value : null,
      plu: formattedPlu
    };
  }

  private getBulkRecordCount(): number | null {
    const totalRecords = this.historicalTable?.totalRecords;
    return typeof totalRecords === 'number' && totalRecords >= 0 ? totalRecords : null;
  }

  private _updateControlValidation() {
    const supplierControl = this.formGroup.get('supplierControl');
    const pluControl = this.formGroup.get('pluControl');

    if (!supplierControl.value && !pluControl.value) {
      supplierControl.setValidators([Validators.required]);
      pluControl.setValidators([Validators.required]);
    } else {
      supplierControl.clearValidators();
      pluControl.clearValidators();
    }
    // Update validity without emitting value change events to avoid infinite loops
    supplierControl.updateValueAndValidity({ emitEvent: false });
    pluControl.updateValueAndValidity({ emitEvent: false });
  }

  isFormValid(): boolean {
    const storeControlValid = this.formGroup.get('storeControl').valid;
    const supplierControlValue = this.formGroup.get('supplierControl').value;
    const pluControlValue = this.formGroup.get('pluControl').value;

    // Check if the supplierControl value matches any supplier in the filtered list
    const isSupplierValid = this.suppliers.some(supplier => supplier.label === supplierControlValue);

    // Ensure that store control is valid, and either the supplier is selected or PLU has a value
    return storeControlValid && (isSupplierValid || pluControlValue);
  }
}

class Store {
  storeId: number;
  storeName: string;
}

class Supplier {
  value: number;
  label: string;
}
